import React from 'react';
import NumberFormat from 'react-number-format';
import { Currency, CurrencyVariant, Percentage } from 'components/core';
import { useFixedIncomeTranscations } from 'modules/2025-q3/fixed-income-transactions/use-fixed-income-transactions';
import { useBusiness } from 'hooks/business/use-business';
import {
  Fund,
  otherPositionsTNoteColumns,
} from 'modules/dashboard/treasure-reserve/portfolio/components/portfolio-view/components/product-positions/product-positions';
import { ColumnType, DashboardTable } from 'modules/dashboard/components';
import { dayjs } from 'app/dayjs';
import { Box } from '@material-ui/core';
import { DownloadCSVJson } from 'modules/2025-q3/download-csv-json/download-csv-json';
import { Button } from 'modules/2023-q3/components/button/button';
import {
  ButtonContent,
  SupportedIcons,
} from 'modules/2023-q3/components/button/button-content/button-content';

type FixedIncomeTranscation = {
  buy_or_sell: string;
  closing_price: string;
  price: string;
  transaction_date: string;
  quantity: string;
};

const transactionColumns: ColumnType[] = [
  { name: 'Transactions', size: 2 },
  { alignment: 'right', name: 'Quantity', size: 2 },
  { alignment: 'right', name: 'Price', size: 2 },
  { alignment: 'right', name: 'Total Price', size: 2 },
  { alignment: 'right', name: 'P/L', size: 2 },
  { alignment: 'right', name: 'Market Value', size: 2 },
];

export const FixedIncomeList: React.FC<{
  funds: any;
}> = ({ funds }) => {
  const { data: business, isLoading: isLoadingBusiness } = useBusiness();
  const {
    data: fixedIncomeTranscations,
    isLoading: isLoadingFixedIncomeTranscations,
  } = useFixedIncomeTranscations(business?.Id);

  if (isLoadingBusiness || isLoadingFixedIncomeTranscations) {
    return null;
  }

  const csvData: {}[] = [];

  fixedIncomeTranscations.map((transaction: any) => {
    const today = dayjs().format('YYYY-MM-DD');
    const quantityDivided = Number(transaction.quantity) / 100;
    const totalPrice = Number(quantityDivided) * Number(transaction.price);
    const marketValue =
      Number(quantityDivided) * Number(transaction.closing_price);
    const fund = funds.find((fund: Fund) => fund.cusip === transaction.cusip);

    return csvData.push({
      transaction_date: dayjs(transaction.transaction_date).format(
        'YYYY-MM-DD',
      ),
      fund_name: transaction.fund.name,
      fund_cusip: transaction.fund.cusip,
      fund_market_value: fund?.dollarValue,
      fund_maturity_date: transaction.fund.maturityDate,
      fund_coupon_rate: transaction.fund.coupon_rate,
      buy_or_sell: transaction.buy_or_sell,
      quantity: transaction.quantity,
      price: transaction.price,
      total_price: totalPrice.toFixed(2),
      market_value_date: today,
      market_value: marketValue.toFixed(2),
      p_l: Number(marketValue - totalPrice).toFixed(2),
    });
  });

  return (
    <>
      <Box mb={3} textAlign="right">
        <DownloadCSVJson
          filename={`treasure_tnote_transactions_${dayjs().format(
            'YYYY-MM-DD',
          )}.csv`}
          json={csvData}
        >
          <Button size="small">
            <ButtonContent iconStart={SupportedIcons.Download}>
              Download CSV
            </ButtonContent>
          </Button>
        </DownloadCSVJson>
      </Box>

      {funds.map((fund: Fund) => {
        const tableData = [];

        tableData.push([
          fund.name,
          fund.cusip,
          fund.maturityDate
            ? dayjs(fund.maturityDate).format('MMMM D, YYYY')
            : null,
          fund.coupon_rate ? (
            <Percentage decimalScale={3} number={+fund.coupon_rate} />
          ) : null,
          <Currency
            number={Number(fund.dollarValue)}
            variant={CurrencyVariant.Full}
          />,
        ]);

        const transactions = fixedIncomeTranscations.filter(
          (x: { cusip: string }) => x.cusip === fund.cusip,
        );

        const transactionTableData: [
          string,
          React.ReactElement,
          React.ReactElement,
          React.ReactElement,
          React.ReactElement,
          React.ReactElement,
        ][] = [];

        transactions.map((transaction: FixedIncomeTranscation) => {
          const quantityDivided = Number(transaction.quantity) / 100;
          const totalPrice =
            Number(quantityDivided) * Number(transaction.price);
          const marketValue =
            Number(quantityDivided) * Number(transaction.closing_price);

          return transactionTableData.push([
            dayjs(transaction.transaction_date).format('MMMM D, YYYY'),
            <NumberFormat
              displayType="text"
              thousandSeparator
              value={transaction.quantity}
            />,
            <Currency
              decimalScale={3}
              number={Number(transaction.price)}
              variant={CurrencyVariant.FullWithDecimalScale}
            />,
            <Currency number={totalPrice} variant={CurrencyVariant.Full} />,
            <Currency
              number={Number(marketValue - totalPrice)}
              variant={CurrencyVariant.Full}
            />,
            <Currency number={marketValue} variant={CurrencyVariant.Full} />,
          ]);
        });

        if (fixedIncomeTranscations.length < 1) {
          return null;
        }

        return (
          <Box mb={6}>
            <DashboardTable
              columns={otherPositionsTNoteColumns}
              data={tableData}
            />

            <Box mt={2}>
              <DashboardTable
                columns={transactionColumns}
                data={transactionTableData}
              />
            </Box>
          </Box>
        );
      })}
    </>
  );
};
